import type { ActionFunctionArgs } from "react-router";
import type { EventFollow } from "@repo/shared-types";
import { api } from "@/lib/api";
import { errorMessage } from "@/lib/session";
import { toast } from "@/lib/toast";

/**
 * Following an event (0039). No page of its own: reached by fetchers from
 * wherever a follow control is drawn — the event's page and every card on a
 * feed — so one action answers them all. Every answer says how the caller
 * follows the event now, which the control draws until the page reads the
 * event again.
 *
 * Intents:
 *   follow     all three on; a toast names the event (`title`)
 *   choose     what tells them: `comments`, `changes`, `results`, each
 *              "true" or "false", every tap sending all three
 *   unfollow
 */

export type FollowOutcome = { follow?: EventFollow | null; error?: string };

export async function followAction({ request, params }: ActionFunctionArgs): Promise<FollowOutcome> {
  const form = await request.formData();
  const events = api.space(params.slug ?? "").events;
  const eventId = params.event ?? "";
  const choice = (name: string) => form.get(name) === "true";
  try {
    switch (form.get("intent")) {
      case "follow": {
        const follow = await events.follow(eventId);
        toast(`Following ${String(form.get("title") ?? "the event")}.`);
        return { follow };
      }
      case "choose": {
        const follow = await events.follow(eventId, {
          comments: choice("comments"),
          changes: choice("changes"),
          results: choice("results"),
        });
        toast("Saved.");
        return { follow };
      }
      case "unfollow":
        await events.unfollow(eventId);
        toast("Unfollowed.");
        return { follow: null };
      default:
        throw new Response("Bad Request", { status: 400 });
    }
  } catch (error) {
    if (error instanceof Response) throw error;
    return { error: errorMessage(error, "Could not change how you follow it") };
  }
}
